import React, { useEffect, useState } from "react";
import { api } from "../../../software/api";
import { toast } from "sonner";
import { Search, X, User } from "lucide-react";

export const backend = process.env.REACT_APP_BACKEND_URL;
export const money = (n) => `$${Number(n || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Rewrites legacy preview hosts to the current backend for /api/files assets.
export const imgUrl = (u) => {
  if (!u) return "";
  const i = u.indexOf("/api/files/");
  if (i >= 0) return `${backend}${u.slice(i)}`;
  if (u.startsWith("http")) return u;
  return `${backend}${u}`;
};

// Boxed "Label: value" cell styled like the MS Access read-only text box.
export const CatBox = ({ label, value, labelW = 88 }) => (
  <div className="grid items-center gap-1.5 text-[12px] mb-1" style={{ gridTemplateColumns: `${labelW}px 1fr` }}>
    <span className="text-slate-800 text-right font-semibold whitespace-nowrap">{label}:</span>
    <span className="px-2 py-1 border border-[#b9b9b9] bg-white truncate min-h-[26px]" title={value ?? ""}>{value ?? ""}</span>
  </div>
);

// Photo box with graceful fallback when the legacy image 404s.
export const CatImage = ({ url, alt, className = "" }) => {
  const [broken, setBroken] = useState(false);
  const src = imgUrl(url);
  if (!src || broken) return <div className={`bg-slate-50 flex items-center justify-center text-slate-300 text-[11px] ${className}`}>No photo</div>;
  return <img src={src} alt={alt} onError={() => setBroken(true)} className={`object-cover ${className}`} />;
};

// Client picker → creates a draft quote for the chosen item. Legacy "Create Quotation" button.
export function CreateQuoteModal({ title, item, onClose }) {
  const [q, setQ] = useState("");
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (q.trim().length < 2) { setClients([]); return; }
    let ok = true; setLoading(true);
    const t = setTimeout(() => {
      api("GET", `/clients?q=${encodeURIComponent(q)}&limit=25`)
        .then((r) => { if (ok) setClients(r || []); })
        .catch(() => { if (ok) setClients([]); })
        .finally(() => { if (ok) setLoading(false); });
    }, 250);
    return () => { ok = false; clearTimeout(t); };
  }, [q]);

  const create = async (c) => {
    setBusy(true);
    try {
      const quote = await api("POST", "/quotes", {
        client_id: c.id, status: "draft",
        items: [{ product_id: item.product_id || null, name: item.name, qty: 1, unit_price: item.price, discount: 0 }],
      });
      toast.success(`Quote ${quote.quote_number} created for ${c.name}`);
      onClose();
    } catch (e) { toast.error(e.message || "Could not create quote"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[130] bg-black/50 flex items-center justify-center p-4" data-testid="create-quote-modal" onClick={onClose}>
      <div className="w-full max-w-lg bg-[#ece9d8] rounded-sm shadow-2xl border border-slate-500 overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between bg-gradient-to-b from-[#2a6cb5] to-[#1a4f8a] px-3 py-2">
          <span className="text-white font-black text-sm">Create Quotation — {title}</span>
          <button onClick={onClose} data-testid="create-quote-close" className="text-white/90 hover:text-white"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-4">
          <p className="text-[12px] text-slate-700 mb-2">Quoting <b>{item.name}</b> at <b>{money(item.price)}</b>. Search a client to raise the quotation.</p>
          <div className="relative mb-2">
            <Search className="h-4 w-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} data-testid="create-quote-search" placeholder="Type a client name, phone or email…" className="w-full rounded-sm border border-[#7ba7d9] bg-white pl-8 pr-3 py-2 text-[13px] outline-none focus:border-[#123a63]" />
          </div>
          <div className="max-h-64 overflow-y-auto bg-white border border-[#b9b9b9] rounded-sm divide-y divide-slate-100">
            {q.trim().length < 2 ? <p className="text-[12px] text-slate-400 p-3">Start typing to find a client…</p>
              : loading ? <p className="text-[12px] text-slate-400 p-3">Searching…</p>
              : clients.length === 0 ? <p className="text-[12px] text-slate-400 p-3">No matching clients.</p>
              : clients.map((c) => (
                <button key={c.id} disabled={busy} onClick={() => create(c)} data-testid={`create-quote-client-${c.id}`} className="w-full text-left px-3 py-2 text-[13px] hover:bg-sky-50 flex items-center gap-2 disabled:opacity-50">
                  <User className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                  <span className="font-semibold text-slate-800 truncate">{c.name}</span>
                  {(c.phone || c.mobile) && <span className="text-slate-400 text-[11px] ml-auto shrink-0">{c.phone || c.mobile}</span>}
                </button>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Reusable orange top bar for the Sauna / Gazebo / Parts search forms.
export const OrangeBar = ({ children }) => (
  <div className="flex flex-wrap items-center gap-x-6 gap-y-2 px-4 py-3" style={{ background: "linear-gradient(180deg,#f0a52c,#e89018)" }}>
    {children}
  </div>
);

export const barSelect = "border border-[#c98f00] bg-white text-slate-900 px-2 py-1 text-[12px] rounded-sm outline-none focus:border-[#123a63] min-w-[150px]";
export const barNum = "border border-[#c98f00] bg-white text-slate-900 px-2 py-1 text-[12px] rounded-sm outline-none focus:border-[#123a63] w-32 text-right";
export const barBtn = "rounded-sm bg-white border border-white/70 px-4 py-1.5 text-[13px] font-black text-[#1a4f8a] hover:bg-sky-50 shadow";
